import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import { IPageDataBoxType, IAnswerItem, ILimitItem, IGenericObj, IhightLightInfo } from '@/utils/ctype';
import { getWritingContent, getAnswerList, saveAnswerList, submitAnswer } from '@/api/writing';
import type { ExamRequestOptions } from '@/api/type';
import {fetchWritingAnalysis} from '@/api/ai';
import {renderHightLightPage, commonReducers} from './common';

export interface IListeningTopicListState {
  showNotePanel: boolean;
  pageDataAll: any[];
  pageDataCache: any[];
  pageData: IPageDataBoxType | null;
  recordId: number;
  limits: ILimitItem[];
  answerAll: Array<IAnswerItem| null | undefined>;
  answerMap:IGenericObj<string[]>;
  currentFocus: number;
  pageDataIndex: number;
  loadAnswered: boolean;
  highLightInfo: IhightLightInfo;
  surplus_time: number;
  essay: string;
  big_essay: string;
  is_confirm: number;
  analysisBigEssay: any;
  analysisEssay: any;
  draftSyncedAt: number;
  draftSavePending: boolean;
}

type EssayType = 'essay' | 'big_essay';

const initialState: IListeningTopicListState = {
  showNotePanel: false,
  pageDataCache: [],
  pageDataAll: [],
  pageData: null,
  limits: [],
  answerAll: [],
  answerMap: {},
  currentFocus: 1,
  recordId: 1,
  pageDataIndex: 0,
  loadAnswered: false,
  highLightInfo: {},
  surplus_time: 60 * 60,
  essay: '',
  big_essay: '',
  is_confirm: 0,
  analysisBigEssay: {},
  analysisEssay: {},
  draftSyncedAt: 0,
  draftSavePending: false,
};


export const getAnswerListAsync = createAsyncThunk<any, {record_id: number;}>('writing/getAnswerList', async (params, {rejectWithValue, getState}) => {
  try {
    const res = await getAnswerList(params);
    if (res.code !== 200) {
      return rejectWithValue(null);
    }
    return res.data;
  } catch (err) {
    return rejectWithValue(null);
  }
});


export const getWritingAsync = createAsyncThunk<any, {record_id: number;}>('writing/getPageData', async (params, {rejectWithValue, getState}) => {
  try {
    const res = await getWritingContent(params);
    if (res.code !== 200) {
      return rejectWithValue(null);
    }
    return res.data.list;
  } catch (err) {
    return rejectWithValue(null);
  }
});

export const saveAnswerListAsync = createAsyncThunk<unknown, ExamRequestOptions | void>(
  'writing/saveAnswerListAsync',
  async (params, {rejectWithValue, getState, signal}) => {
    try {
      const state: any = getState();
      const recordId = Number(state.writing.recordId);
      if (params?.recordId !== undefined && Number(params.recordId) !== recordId) {
        return rejectWithValue(null);
      }
      const res = await saveAnswerList({
        record_id: recordId,
        essay: state.writing.essay,
        big_essay: state.writing.big_essay,
        mark: state.writing.highLightInfo ? JSON.stringify(state.writing.highLightInfo) : '',
        surplus_time: state.writing.surplus_time,
        is_confirm: state.writing.is_confirm
      }, {signal: params?.signal ?? signal});
      if (res.code !== 200) {
        return rejectWithValue(null);
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(null);
    }
  }, {
    condition: (_params, {getState}) => !(getState() as any).writing.draftSavePending,
  });

export const submitAnswerAsync = createAsyncThunk<any, ExamRequestOptions | void>('writing/submitAnswer', async (params, {rejectWithValue, getState, signal}) => {
  try {
    const recordId = Number((getState() as any).writing.recordId);
    if (params?.recordId !== undefined && Number(params.recordId) !== recordId) {
      return rejectWithValue(null);
    }
    const res = await submitAnswer({record_id: recordId}, {signal: params?.signal ?? signal});
    if (res.code !== 200) {
      return rejectWithValue(null);
    }
    return res.data;
  } catch (err) {
    return rejectWithValue(null);
  }
});

export const fetchWritingAnalysisAsync = createAsyncThunk<any, {record_id: number;}>('writing/fetchWritingAnalysis', async (params, {rejectWithValue, getState}) => {
  try {
    const res = await fetchWritingAnalysis(params);
    if (res.code !== 200) {
      return rejectWithValue(null);
    }
    return res.data;
  } catch (err) {
    return rejectWithValue(null);
  }
});

export const writingSlice = createSlice({
  name: 'writing',
  initialState,
  reducers: {
    ...commonReducers,
    saveAnswer: (state, {payload}: {payload: {key: string; value: string;};}) => {
      const {key , value} = payload;
      state[key as EssayType] = value;
      if (key === 'essay') {
        state.answerAll[0]!.answer = [value];
      } else {
        state.answerAll[1]!.answer = [value];
      }
    },
    
  },
  extraReducers: (builder) => {
    const dealAnwer = (state: IListeningTopicListState) => {
      // if (state.pageDataAll && state.loadAnswered) {
      //   state.answerAll = state.pageDataAll.map(item=> {
      //     return {id: item.id, answer: ['']};
      //   });
      // }
      
      const curPageData = state.pageDataAll[state.pageDataIndex];
      const highLightObj = state.highLightInfo[state.pageDataIndex];
      if (curPageData && highLightObj) {
        const cloneCurPageData = _.cloneDeep(curPageData);
        renderHightLightPage(highLightObj, cloneCurPageData);
        const pageDataCache = [...state.pageDataCache];
        pageDataCache[state.pageDataIndex] = cloneCurPageData;
        state.pageDataCache = pageDataCache;
      }
    };

   
    builder.addCase(getWritingAsync.fulfilled, (state, action) => {
      // 预处理
      if (action.payload && action.payload[1]) {
        if (action.payload[0]) {
          action.payload[0].question.frontContent = '';
          action.payload[0].question.afterContent = 'Write at least 150 words.';
        }
        if (action.payload[1]) {
          action.payload[1].question.frontContent = 'Write about the following topic:';
          action.payload[1].question.afterContent = 'Give reasons for your answer and include any relevant examples from your own knowledge and experience. \nWrite at least 250 words.';
        }
      }

      state.pageDataAll = action.payload;
      state.pageDataIndex = 0;
      if (state.pageDataAll) {
        state.pageDataCache = [state.pageDataAll[state.pageDataIndex]];
        state.pageData = state.pageDataAll[state.pageDataIndex];
        state.limits = [{min: 1, max: 1}, {min: 2, max: 2}];
      }
      dealAnwer(state);
    }).addCase(getAnswerListAsync.fulfilled, (state, action) => {
      state.loadAnswered = true;
      const data = action.payload;
      const {answer, surplus_time, mark, essay, big_essay, is_confirm} = data;
      state.surplus_time = surplus_time || 60 * 60;
      state.highLightInfo = mark ? JSON.parse(mark) : {};
      state.answerAll = answer || [{answer: [essay]}, {answer: [big_essay]}];
      state.essay = essay;
      state.big_essay = big_essay;
      state.is_confirm = is_confirm;
      state.draftSyncedAt = Date.now();
      dealAnwer(state);
    }).addCase(saveAnswerListAsync.pending, (state) => {
      state.draftSavePending = true;
    }).addCase(saveAnswerListAsync.fulfilled, (state) => {
      state.draftSavePending = false;
      state.draftSyncedAt = Date.now();
    })
      .addCase(saveAnswerListAsync.rejected, (state) => {
        state.draftSavePending = false;
      })
      .addCase(submitAnswerAsync.fulfilled, ()=>{}).addCase(fetchWritingAnalysisAsync.fulfilled, (state, action) => {
        if (action.payload) {
          state.analysisBigEssay = action.payload.big_essay_info;
          state.analysisEssay = action.payload.essay_info;
        }
      });
  },
});


export const {toggleShowNotePanel, setPanelDataByIndex, setFocusIndex, saveAnswer,
  updateTimeState, updateRecordId, showDialog, hideDialog, saveHighlightData,
  clearHightLightInfo, updatePanelInfo, deleteNote} = writingSlice.actions;
